/**
 * HintUtil - word hint generation for QR_Hra__Hint generators
 * Ported from C# EduFrame.Common.HintUtil
 */

export class HintUtil {
  public static getHintsUnique(
    words: string[],
    getHints: (word: string) => string[]
  ): Map<string, string> {
    const w = new Map(words.map((word) => [word, new Set(getHints(word))]));
    const all = new Set<string>();
    const countMap = new Map<string, number>();
    w.forEach((hints) => {
      hints.forEach((h) => countMap.set(h, (countMap.get(h) ?? 0) + 1));
    });
    countMap.forEach((count, h) => {
      if (count > 1) all.add(h);
    });

    const result = new Map<string, string>();
    w.forEach((hints, word) => {
      let unique: string | undefined;
      hints.forEach((h) => {
        if (!all.has(h) && unique === undefined) unique = h;
      });
      result.set(word, unique ?? word);
    });
    return result;
  }

  public static splitCZ(text: string): string[] {
    const list: string[] = [];
    const upper = text.toUpperCase();
    for (let i = 0; i < upper.length; i++) {
      if (i < upper.length - 1 && upper[i] === 'C' && upper[i + 1] === 'H') {
        list.push('CH');
        i++;
      } else {
        list.push(upper[i]);
      }
    }
    return list;
  }

  public static getHintsCZ(word: string): string[] {
    const hints: string[] = [];
    const upper = HintUtil.splitCZ(word);
    const upperWord = upper.join('');

    if (upperWord === upper.slice().reverse().join('')) hints.push('Slovo je palindrom.');

    const firstEqualsLast = upper[0] === upper[upper.length - 1];
    if (firstEqualsLast) {
      hints.push(`První a poslední písmeno: ${upper[0]}`);
    }

    const subwords = [
      'STŘED',
      'SPOLU',
      'KŘÍŽ',
      'VIDEO',
      'STROJ',
      'KROK',
      'PLÁN',
      'VLAK',
      'HRAD',
      'MRAK',
      'STŮL',
      'ČAS',
      'KAM',
      'ZVUK',
      'TMA',
      'CHOD',
      'DÍL',
      'STAV',
      'KOS',
      'NOC',
      'OKO',
      'SEN',
      'ROK',
      'LET',
      'MÍR',
      'KUS',
      'TIS',
      'DAR',
      'VÍR',
      'KREV',
      'KOST',
      'MOST',
      'KOLO',
      'LIST',
      'GRAF',
      'PŘES',
      'POLO',
      'KLAD',
      'PORT',
      'AUTO',
      'SVĚT',
      'SOVA',
      'PLAT',
      'STAN',
      'TÁNÍ',
      'POLE',
      'UCHO',
      'VINA',
      'KRÁT',
      'MEZI',
      'HROM',
      'KÁVA',
      'TELE',
      'KLÁDA',
      'OKOV',
      'OPIS',
      'HLAD',
      'DOST',
      'MOTO',
      'KOV',
      'LOV',
      'NOV',
      'NOS',
      'NIC',
      'LEN',
      'VEN',
      'KAT',
      'ONI',
      'ALE',
      'MAT',
      'DNO',
      'ARA',
      'ERA',
      'OKO',
      'LIS',
      'DEN',
      'HOD',
      'PAT',
      'ROD',
      'GEN',
      'STO',
    ];
    const subword = subwords.find((s) => upper.includes(s) && s !== upperWord);
    if (subword !== undefined) hints.push(`Obsahuje ${subword}`);

    const leastCommon = ['Q', 'W', 'X', 'Ď', 'Ť', 'Ň', 'F', 'G', 'Ó', 'Ú', 'Ů'];
    for (const lc of leastCommon) {
      if (upper.includes(lc)) hints.push(`Obsahuje ${lc}`);
    }

    if (!firstEqualsLast) {
      hints.push(`První písmeno: ${upper[0]}`);
      hints.push(`Poslední písmeno: ${upper[upper.length - 1]}`);
    }

    for (let i = 0; i < upper.length - 1; i++) {
      if (upper[i] === upper[i + 1]) hints.push(`Obsahuje ${upper[i]}${upper[i + 1]}`);
    }

    const isWhitespace = (ch: string) => ch.length === 1 && /\s/.test(ch);
    const letterCounts = new Map<string, number>();
    upper
      .filter((c) => !isWhitespace(c))
      .forEach((c) => letterCounts.set(c, (letterCounts.get(c) ?? 0) + 1));
    const letterCountArr = Array.from(letterCounts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);

    const chars = (n: number) =>
      `${n} ${n === 1 ? 'písmeno' : n >= 2 && n <= 4 ? 'písmena' : 'písmen'}`;

    for (const { key, count } of letterCountArr.filter((x) => x.count > 2)) {
      hints.push(`Obsahuje ${chars(count)} ${key}`);
    }

    if (upper.every((c) => !isWhitespace(c))) hints.push(`Délka: ${chars(upper.length)}`);
    if (upper.length >= 2 && !isWhitespace(upper[1])) hints.push(`Druhý znak: ${upper[1]}`);

    for (const { key, count } of letterCountArr.filter((x) => x.count === 2)) {
      if (!firstEqualsLast || key !== upper[0]) hints.push(`Obsahuje ${chars(count)} ${key}`);
    }

    const trigraphs = [
      'PRO',
      'PRA',
      'STA',
      'OST',
      'OVA',
      'NÍK',
      'SPO',
      'STR',
      'STO',
      'ATI',
      'ENE',
      'TEL',
      'ŠTĚ',
      'NÍC',
      'ELE',
    ];
    for (const tg of trigraphs) {
      if (upper.includes(tg)) hints.push(`Obsahuje ${tg}`);
    }

    if (upper.length > 4 && upper.slice(0, 3).every((c) => !isWhitespace(c))) {
      hints.push(`Začíná na ${upper.slice(0, 3).join('')}`);
    }

    for (let i = 2; i < upper.length - 1; i++) {
      const nth = i === 2 ? 'Třetí' : i === 3 ? 'Čtvrtý' : i === 4 ? 'Pátý' : `${i + 1}.`;
      if (!isWhitespace(upper[i])) hints.push(`${nth} znak je ${upper[i]}`);
    }

    return hints;
  }

  public static getHintsEN(word: string): string[] {
    const hints: string[] = [];
    const upper = word.toUpperCase().split('');
    const upperWord = upper.join('');

    if (upperWord === upper.slice().reverse().join('')) hints.push('Word is palindrom');

    const firstEqualsLast = upper[0] === upper[upper.length - 1];
    if (firstEqualsLast) {
      hints.push(`First and last letter: ${upper[0]}`);
    }

    const leastCommon = ['Z', 'Q', 'X', 'J', 'V'];
    for (const lc of leastCommon) {
      if (upper.includes(lc)) hints.push(`Contains ${lc}`);
    }

    if (!firstEqualsLast) {
      hints.push(`First letter: ${upper[0]}`);
      hints.push(`Last letter: ${upper[upper.length - 1]}`);
    }

    for (let i = 0; i < upper.length - 1; i++) {
      if (upper[i] === upper[i + 1]) hints.push(`Contains ${upper[i]}${upper[i + 1]}`);
    }

    const isWhitespace = (ch: string) => /\s/.test(ch);
    const letterCounts = new Map<string, number>();
    upper
      .filter((c) => !isWhitespace(c))
      .forEach((c) => letterCounts.set(c, (letterCounts.get(c) ?? 0) + 1));
    const letterCountArr = Array.from(letterCounts.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);

    const chars = (n: number) => `${n} ${n === 1 ? 'letter' : 'letters'}`;

    for (const { key, count } of letterCountArr.filter((x) => x.count > 2)) {
      hints.push(`Contains ${chars(count)} ${key}`);
    }

    if (upper.every((c) => !isWhitespace(c))) hints.push(`Length: ${chars(upper.length)}`);
    if (upper.length >= 2 && !isWhitespace(upper[1])) hints.push(`Second letter: ${upper[1]}`);

    for (const { key, count } of letterCountArr.filter((x) => x.count === 2)) {
      if (!firstEqualsLast || key !== upper[0]) hints.push(`Contains ${chars(count)} ${key}`);
    }

    const trigraphs = [
      'THE',
      'ING',
      'AND',
      'ENT',
      'ION',
      'TIO',
      'FOR',
      'NDE',
      'NCE',
      'MEN',
      'COM',
      'EVE',
      'RES',
      'VER',
      'CON',
      'STR',
      'EST',
      'OUS',
      'IVE',
      'ALL',
    ];
    for (const tg of trigraphs) {
      if (upper.includes(tg)) hints.push(`Contains ${tg}`);
    }

    if (upper.length > 4 && upper.slice(0, 3).every((c) => !isWhitespace(c))) {
      hints.push(`Starts with ${upper.slice(0, 3).join('')}`);
    }

    for (let i = 2; i < upper.length - 1; i++) {
      const nth = i === 2 ? 'Third' : i === 3 ? 'Fourth' : i === 4 ? 'Fifth' : `${i + 1}th`;
      if (!isWhitespace(upper[i])) hints.push(`${nth} letter is ${upper[i]}`);
    }

    return hints;
  }
}
